import { onBeforeUnmount, onMounted, readonly, ref, type Ref } from 'vue';

// One shared one-second ticker for every Countdown on the page, so thirty cards don't
// run thirty intervals. It runs while anything uses it and the tab is visible, and
// ticks at once when the tab comes back. Read the clock on each tick rather than
// counting ticks, so drift can't build up.

const tick = ref(0);
let users = 0;
let interval: ReturnType<typeof setInterval> | undefined;

const bump = (): void => {
  tick.value += 1;
};

function run(): void {
  if (interval !== undefined || users === 0 || document.visibilityState === 'hidden') return;
  bump();
  interval = setInterval(bump, 1000);
}

function pause(): void {
  clearInterval(interval);
  interval = undefined;
}

function onVisibility(): void {
  if (document.visibilityState === 'hidden') pause();
  else run();
}

/** A counter that goes up once a second while the page is visible. Depend on it to recompute. */
export function useTicker(): Readonly<Ref<number>> {
  onMounted(() => {
    users += 1;
    if (users === 1) document.addEventListener('visibilitychange', onVisibility);
    run();
  });
  onBeforeUnmount(() => {
    users -= 1;
    if (users === 0) {
      pause();
      document.removeEventListener('visibilitychange', onVisibility);
    }
  });
  return readonly(tick);
}
